import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import styles from "../static/styles/WeeklyTournamentJoin.module.css";
import calendarIcon from "../static/img/Home/calendarIcon.svg";
import aimTrainCover from "../static/img/MadeByESL/coverAimTrain.avif";
import cannonClashCover from "../static/img/MadeByESL/coverCannonClash.avif";
import { eslTournamentCheckin } from "../api/tournaments";
import { trackTournamentSignup } from "../utils/analytics";
import MessagePopup from "../components/MessagePopup";

const formatDateTime = (dateTimeString) => {
  if (!dateTimeString) return { date: "N/A", time: "N/A" };
  const [datePart, timePart, ampm] = dateTimeString.split(" ");
  return { date: datePart, time: `${timePart} ${ampm}` };
};

const getTournamentImage = (tournament) => {
  if (tournament.tournament_game === "cannon_clash_001")
    return cannonClashCover;
  if (tournament.tournament_game === "aim_train_001") return aimTrainCover;
  if (tournament.thumbnail) return tournament.thumbnail;
  return aimTrainCover;
};

const EslTournamentJoin = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const tournament = location.state?.tournament;

  const [cnic, setCnic] = useState("");
  const [address, setAddress] = useState("");
  const [userEmail, setUserEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [popupMessage, setPopupMessage] = useState(null);
  const [isPopupOpen, setIsPopupOpen] = useState(false);
  const [errors, setErrors] = useState({});

  useEffect(() => {
    document.title = "Join Tournament | Jazz Esl";
  }, []);

  if (!tournament) {
    return (
      <div className={styles.error}>
        No tournament data. Please go back and try again.
      </div>
    );
  }

  const bgImage = getTournamentImage(tournament);
  const { date, time } = formatDateTime(tournament.time);
  const isEnded = tournament.status === 4;
  const isRunning = tournament.status === 2;

  const handleRegister = async () => {
    if (loading) return;

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    const newErrors = {};
    const errorMessages = [];

    if (cnic.trim().length !== 13) {
      newErrors.cnic = true;
      errorMessages.push("CNIC must be exactly 13 digits");
    }
    if (!address.trim()) {
      newErrors.address = true;
      errorMessages.push("Address is required");
    }
    if (!userEmail.trim()) {
      newErrors.userEmail = true;
      errorMessages.push("Email is required");
    } else if (!emailRegex.test(userEmail.trim())) {
      newErrors.userEmail = true;
      errorMessages.push("Please enter a valid email address");
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      setPopupMessage(errorMessages.join("\n"));
      setIsPopupOpen(true);
      return;
    }

    setErrors({});
    setLoading(true);

    try {
      const response = await eslTournamentCheckin(tournament.tourn_id, {
        cnic: cnic.trim(),
        address: address.trim(),
        userEmail: userEmail.trim(),
      });

      if (response?.data?.already_submited) {
        setPopupMessage("You are already registered for this tournament.");
      } else {
        trackTournamentSignup(
          tournament.tourn_name,
          tournament.tourn_id,
          "ESL",
          tournament.tournament_game,
        );
        setPopupMessage("Successfully registered for the tournament!");
      }
    } catch (error) {
      setPopupMessage(
        error.message || "Failed to register for the tournament.",
      );
    } finally {
      setLoading(false);
      setIsPopupOpen(true);
    }
  };

  return (
    <div className={styles.container}>
      <div className={styles.mainImageContainer}>
        <div
          className={`${styles.mainImage} ${isEnded ? styles.endedImage : ""}`}
          style={{
            "--bg-image": `url(${bgImage})`,
            backgroundImage: `url(${bgImage})`,
          }}
        >
          {isEnded && (
            <div className={styles.highlightsOverlay}>
              <p>Tournament has ended</p>
            </div>
          )}
          {isRunning && (
            <div className={styles.highlightsOverlay}>
              <p>Tournament is in progress</p>
            </div>
          )}
        </div>
      </div>
      <div className={styles.joinSection}>
        <div
          className={styles.joinBackground}
          style={{ "--background-image": `url(${bgImage})` }}
        >
          <div className={styles.overlay}>
            <div className={styles.topContent}>
              {tournament.tourn_mode !== "special" && (
                <div className={styles.prizeInfo}>
                  <h3>
                    Entry Fee:{" "}
                    {tournament.entryFee
                      ? `${tournament.entryFee} Credits`
                      : "Free Entry"}
                  </h3>
                  <div className={styles.prizes}>
                    {tournament.prize && <p>1st: {tournament.prize} Credits</p>}
                    {tournament.second_prize && (
                      <p>2nd: {tournament.second_prize} Credits</p>
                    )}
                    {tournament.third_prize && (
                      <p>3rd: {tournament.third_prize} Credits</p>
                    )}
                  </div>
                </div>
              )}
              <div className={styles.participants}>
                <p>
                  ( Participants:{" "}
                  {tournament.entries || tournament.total_checkin || 0} )
                </p>
              </div>
            </div>
            <div className={styles.bottomContent}>
              <div>
                <p>{tournament.tourn_name}</p>
              </div>
              <div className={styles.timeInfo}>
                <div className={styles.startTime}>
                  <img src={calendarIcon} alt="Calendar" />
                  <div>
                    <p>Starting At</p>
                    <p>{date}</p>
                    <p>{time}</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div className={styles.formSection}>
          <input
            type="text"
            inputMode="numeric"
            placeholder="Enter Your CNIC"
            className={`${styles.input} ${
              errors.cnic ? styles.errorInput : ""
            }`}
            value={cnic}
            onChange={(e) => {
              const val = e.target.value.replace(/\D/g, "").slice(0, 13);
              setCnic(val);
              setErrors((prev) => ({ ...prev, cnic: false }));
            }}
            maxLength={13}
            disabled={isEnded || isRunning}
          />
          <input
            type="text"
            placeholder="Enter Your Address"
            className={`${styles.input} ${
              errors.address ? styles.errorInput : ""
            }`}
            value={address}
            onChange={(e) => {
              setAddress(e.target.value);
              setErrors((prev) => ({ ...prev, address: false }));
            }}
            disabled={isEnded || isRunning}
          />
          <input
            type="email"
            placeholder="Enter Your Email"
            className={`${styles.input} ${
              errors.userEmail ? styles.errorInput : ""
            }`}
            value={userEmail}
            onChange={(e) => {
              setUserEmail(e.target.value);
              setErrors((prev) => ({ ...prev, userEmail: false }));
            }}
            disabled={isEnded || isRunning}
          />
          <button
            className={styles.registerButton}
            onClick={handleRegister}
            disabled={loading || isEnded || isRunning}
          >
            {loading
              ? "Please wait..."
              : isEnded || isRunning
              ? "Registrations Closed"
              : "Register"}
          </button>
        </div>
      </div>
      <MessagePopup
        isOpen={isPopupOpen}
        message={popupMessage}
        onClose={() => {
          setIsPopupOpen(false);
          navigate("/tournaments");
        }}
      />
    </div>
  );
};

export default EslTournamentJoin;
